import moment from "moment-timezone";
import { convertUTCToLocalDateTime } from "./dateTimeUtils";

const UTC_LIKE = new Set(["UTC", "GMT", "Z", "ETC/UTC", "ETC/GMT", "GREENWICH"]);

function isUtcLikeTz(tz) {
  if (tz == null || String(tz).trim() === "") return true;
  return UTC_LIKE.has(String(tz).trim().toUpperCase());
}

/** True if `tz` is UTC/GMT-like (API often uses this for storage, not for viewer IANA). */
export function isUtcLikeTimezone(tz) {
  return isUtcLikeTz(tz);
}

function padHms(timeVal) {
  const raw = String(timeVal ?? "").trim();
  const base = raw.includes(".") ? raw.replace(/\.\d+$/, "") : raw;
  const parts = base.split(":");
  const h = String(parts[0] ?? "0").padStart(2, "0");
  const mi = String(parts[1] ?? "0").padStart(2, "0");
  const sec = String(parts[2] ?? "0").replace(/\D/g, "").slice(0, 2).padStart(2, "0") || "00";
  return `${h}:${mi}:${sec}`;
}

function extractYmd(calendarDate) {
  const m = String(calendarDate ?? "").trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

/** Keys that mean "where to render for this user". Omit `tz` — APIs often echo query/storage TZ (e.g. UTC), not the viewer's IANA zone. */
const API_TZ_KEYS = [
  "clientTimeZone",
  "viewerTimeZone",
  "viewerTimezone",
  "requestedTimeZone",
  "displayTimezone",
  "userTimezone",
];

/** First explicit IANA zone on a string or API payload (no Intl fallback). */
export function getClientTimeZoneFromServerPayload(payload) {
  if (payload == null) return null;
  if (typeof payload === "string") {
    const t = payload.trim();
    return t !== "" ? t : null;
  }
  if (typeof payload === "object" && !Array.isArray(payload)) {
    for (const k of API_TZ_KEYS) {
      const v = payload[k];
      if (typeof v === "string" && v.trim() !== "") return v.trim();
    }
  }
  return null;
}

/**
 * Resolves the zone used to format punch/schedule times: explicit non-UTC IANA from payload,
 * else device (Intl), else payload UTC / moment guess.
 * Payload values like `clientTimeZone: "UTC"` usually mean "timestamps are UTC", not "show times in UTC";
 * combined with `data.timezone: "UTC"` that would otherwise show 04:00–13:00 instead of local 9–6.
 */
export function getViewerDisplayTimezone(input) {
  const picked = getClientTimeZoneFromServerPayload(input);
  if (picked && !isUtcLikeTz(picked)) return picked;
  try {
    if (typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && typeof tz === "string") return tz;
    }
  } catch {
    /* ignore */
  }
  if (picked) return picked;
  return moment.tz.guess();
}

/**
 * IANA zone for formatting punch instants (`occurred_at`): same as the attendance details drawer.
 * `todayContext.viewerResolvedTimeZone` often mirrors API `timezone` (UTC for storage); UTC-like
 * values must not win over `getViewerDisplayTimezone`, or list rows show UTC while the drawer shows local.
 */
export function getPunchDisplayTimezone(todayContext) {
  const explicit = todayContext?.viewerResolvedTimeZone;
  if (
    explicit != null &&
    String(explicit).trim() !== "" &&
    !isUtcLikeTz(String(explicit).trim())
  ) {
    return String(explicit).trim();
  }
  return getViewerDisplayTimezone(todayContext);
}

/**
 * Schedule boundary from API (`calendarYmd` + `HH:mm[:ss]` in `sourceTimezone`) → local time-of-day
 * in `displayTimezone`, using only `convertUTCToLocalDateTime` from dateTimeUtils.
 */
export function formatScheduleBoundaryForViewer(
  calendarYmd,
  timeHms,
  sourceTimezone,
  displayTimezone
) {
  const ymd = extractYmd(calendarYmd);
  if (!ymd || timeHms == null || timeHms === "") return "";
  const hms = padHms(timeHms);
  const src = String(sourceTimezone ?? "UTC").trim();
  const displayTz =
    displayTimezone != null && String(displayTimezone).trim() !== ""
      ? String(displayTimezone).trim()
      : getViewerDisplayTimezone(null);

  let utcIso;
  if (isUtcLikeTz(src)) {
    utcIso = `${ymd}T${hms}Z`;
  } else if (moment.tz.zone(src)) {
    const wall = moment.tz(`${ymd}T${hms}`, src);
    if (!wall.isValid()) return "";
    utcIso = wall.utc().toISOString();
  } else {
    utcIso = `${ymd}T${hms}Z`;
  }

  // Moment `.tz()` + explicit format avoids Intl edge cases where time-of-day can match UTC.
  const v = convertUTCToLocalDateTime(utcIso, {
    timezone: displayTz,
    format: "h:mm a",
  });
  if (v === "—" || v === "\u2014" || v == null || v === "") return "";
  return v;
}
